import { useState, useMemo, useEffect, useRef } from "react";
import { trpc } from "../lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { DollarSign, Clock, AlertTriangle, CheckCircle, Plus, Copy, FileText, Upload, Trash2, X, Download, Loader2, Sparkles, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import * as XLSX from "xlsx";
import JsBarcode from "jsbarcode";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";

type Aba = "lancamentos" | "recorrentes" | "categorias" | "fornecedores" | "centros_custo" | "importar" | "relatorios";

type SortDirection = "asc" | "desc";

const COLUNAS_OPCIONAIS_CONTAS_PAGAR = [
  { chave: "id", label: "Código" },
  { chave: "descricao", label: "Descrição" },
  { chave: "fornecedor", label: "Fornecedor" },
  { chave: "emitente", label: "Emitente" },
  { chave: "banco", label: "Banco" },
  { chave: "valor", label: "Valor" },
  { chave: "valorPago", label: "Valor Pago" },
  { chave: "vencimento", label: "Vencimento" },
  { chave: "ultimaAtualizacao", label: "Última Atualização" },
  { chave: "status", label: "Status" },
  { chave: "tipoPagamento", label: "Tipo Pgto" },
  { chave: "parcela", label: "Parcela" },
] as const;

const COLUNAS_CONTAS_PAGAR_STORAGE_KEY = "contas-pagar:colunas-visiveis:v2";
const COLUNAS_CONTAS_PAGAR_STORAGE_KEY_ANTERIOR = "contas-pagar:colunas-visiveis:v1";

function carregarColunasContasPagarVisiveis() {
  const padrao = new Set<string>(COLUNAS_OPCIONAIS_CONTAS_PAGAR.map((coluna) => coluna.chave));
  try {
    const salvas = JSON.parse(localStorage.getItem(COLUNAS_CONTAS_PAGAR_STORAGE_KEY) || "null");
    if (Array.isArray(salvas)) return new Set<string>(salvas);
    const salvasAnteriores = JSON.parse(localStorage.getItem(COLUNAS_CONTAS_PAGAR_STORAGE_KEY_ANTERIOR) || "null");
    return Array.isArray(salvasAnteriores) ? new Set<string>([...salvasAnteriores, "ultimaAtualizacao"]) : padrao;
  } catch {
    return padrao;
  }
}

function SortHeader({ label, ativo, direcao, onClick }: { label: string; ativo: boolean; direcao: SortDirection; onClick: () => void }) {
  return (
    <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={onClick}>
      {label}
      {ativo ? direcao === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
    </button>
  );
}

export default function AccountsPayable() {
  const { user } = useGoogleAuth();
  const [aba, setAba] = useState<Aba>("lancamentos");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const abrirModalLancamento = (id: number | null) => {
    setEditingId(id);
    setShowModal(true);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Contas a Pagar</h1>
      </div>

      {/* Abas */}
      <div className="flex gap-2 border-b pb-2">
        {([
          { id: "lancamentos", label: "Lançamentos" },
          { id: "recorrentes", label: "Recorrentes" },
          { id: "categorias", label: "Categorias" },
          { id: "fornecedores", label: "Fornecedores" },
          { id: "centros_custo", label: "Centros de Custo" },
          { id: "importar", label: "Importar" },
          { id: "relatorios", label: "Relatórios" },
        ] as { id: Aba; label: string }[]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setAba(tab.id)}
            className={`px-4 py-2 rounded-t text-sm font-medium transition-colors ${
              aba === tab.id
                ? "bg-white border border-b-0 text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {aba === "lancamentos" && <AbaLancamentos onAbrirModalLancamento={abrirModalLancamento} userId={user?.id} />}
      {aba === "recorrentes" && <AbaRecorrentes />}
      {aba === "categorias" && <AbaCategorias />}
      {aba === "fornecedores" && <AbaFornecedores />}
      {aba === "centros_custo" && <AbaCentrosCusto />}
      {aba === "importar" && <AbaImportar />}
      {aba === "relatorios" && <AbaRelatorios />}
      {showModal && <ModalLancamento id={editingId} onClose={() => setShowModal(false)} />}
    </div>
  );
}

// ============================================================
// ABA LANÇAMENTOS
// ============================================================
function AbaLancamentos({ onAbrirModalLancamento, userId }: { onAbrirModalLancamento: (id: number | null) => void; userId: number | string | null | undefined }) {
  const [filtroStatus, setFiltroStatus] = useState<string>("");
  const [filtroTipoPagamento, setFiltroTipoPagamento] = useState<string>("");
  const [filtroSearch, setFiltroSearch] = useState("");
  const [filtroDataDe, setFiltroDataDe] = useState("");
  const [filtroDataAte, setFiltroDataAte] = useState("");
  const [emitenteIdsTemporarios, setEmitenteIdsTemporarios] = useState<Set<number>>(new Set());
  const [emitenteIdsAplicados, setEmitenteIdsAplicados] = useState<Set<number>>(new Set());
  const [contaBancariaIdsTemporarios, setContaBancariaIdsTemporarios] = useState<Set<number>>(new Set());
  const [contaBancariaIdsAplicados, setContaBancariaIdsAplicados] = useState<Set<number>>(new Set());
  const [colunasVisiveis, setColunasVisiveis] = useState<Set<string>>(carregarColunasContasPagarVisiveis);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("vencimento");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [showBaixaModal, setShowBaixaModal] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [baixaId, setBaixaId] = useState<number | null>(null);
  const [detalhesId, setDetalhesId] = useState<number | null>(null);
  const [contasSelecionadasRemessa, setContasSelecionadasRemessa] = useState<Set<number>>(new Set());
  const [errosRemessa, setErrosRemessa] = useState<Array<{ contaPagarId: number; descricao: string; motivo: string }>>([]);
  const emitentes = trpc.emitentes.list.useQuery();
  const contasBancarias = trpc.bankAccounts.list.useQuery();

  useEffect(() => {
    localStorage.setItem(COLUNAS_CONTAS_PAGAR_STORAGE_KEY, JSON.stringify(Array.from(colunasVisiveis)));
  }, [colunasVisiveis]);

  const emitentesDisponiveis = (emitentes.data ?? []) as any[];
  const bancosDisponiveis = (contasBancarias.data ?? []) as any[];
  const todosEmitentesSelecionados = emitentesDisponiveis.length > 0 && emitentesDisponiveis.every((emitente) => emitenteIdsTemporarios.has(Number(emitente.id)));
  const todosBancosSelecionados = bancosDisponiveis.length > 0 && bancosDisponiveis.every((banco) => contaBancariaIdsTemporarios.has(Number(banco.id)));
  const alternarEmitenteTemporario = (id: number) => setEmitenteIdsTemporarios((atual) => {
    const proximo = new Set(atual);
    if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
    return proximo;
  });
  const alternarBancoTemporario = (id: number) => setContaBancariaIdsTemporarios((atual) => {
    const proximo = new Set(atual);
    if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
    return proximo;
  });
  const alternarTodosEmitentes = () => setEmitenteIdsTemporarios(todosEmitentesSelecionados ? new Set<number>() : new Set(emitentesDisponiveis.map((emitente) => Number(emitente.id))));
  const alternarTodosBancos = () => setContaBancariaIdsTemporarios(todosBancosSelecionados ? new Set<number>() : new Set(bancosDisponiveis.map((banco) => Number(banco.id))));
  const aplicarFiltroEmitentes = () => { setEmitenteIdsAplicados(new Set(emitenteIdsTemporarios)); setPage(1); };
  const aplicarFiltroBancos = () => {
    setContaBancariaIdsAplicados(todosBancosSelecionados ? new Set<number>() : new Set(contaBancariaIdsTemporarios));
    setPage(1);
  };
  const alternarColuna = (chave: string) => setColunasVisiveis((atual) => {
    const proximo = new Set(atual);
    if (proximo.has(chave)) proximo.delete(chave); else proximo.add(chave);
    return proximo;
  });
  const colunaVisivel = (chave: string) => colunasVisiveis.has(chave);

  const indicadores = trpc.contasPagar.indicadores.useQuery({
    filtroEmitenteIds: emitenteIdsAplicados.size > 0 ? Array.from(emitenteIdsAplicados) : undefined,
    contaBancariaIds: contaBancariaIdsAplicados.size > 0 ? Array.from(contaBancariaIdsAplicados) : undefined,
    status: filtroStatus || undefined,
    tipoPagamento: filtroTipoPagamento || undefined,
    search: filtroSearch || undefined,
    dataVencimentoDe: filtroDataDe || undefined,
    dataVencimentoAte: filtroDataAte || undefined,
  });
  const contas = trpc.contasPagar.list.useQuery({
    filtroEmitenteIds: emitenteIdsAplicados.size > 0 ? Array.from(emitenteIdsAplicados) : undefined,
    contaBancariaIds: contaBancariaIdsAplicados.size > 0 ? Array.from(contaBancariaIdsAplicados) : undefined,
    status: filtroStatus || undefined,
    tipoPagamento: filtroTipoPagamento || undefined,
    search: filtroSearch || undefined,
    dataVencimentoDe: filtroDataDe || undefined,
    dataVencimentoAte: filtroDataAte || undefined,
    page,
    pageSize: 20,
    sortBy: sortBy as any,
    sortDir,
  });

  const utils = trpc.useUtils();
  const cancelarMut = trpc.contasPagar.cancelar.useMutation({
    onSuccess: () => { toast.success("Conta cancelada"); utils.contasPagar.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const duplicarMut = trpc.contasPagar.duplicar.useMutation({
    onSuccess: () => { toast.success("Conta duplicada"); utils.contasPagar.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const gerarRecorrentesMut = trpc.contasPagar.gerarRecorrentes.useMutation({
    onSuccess: (res) => { toast.success(`${res.gerados} conta(s) recorrente(s) gerada(s)`); utils.contasPagar.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const criarRascunhosRemessaMut = trpc.remessasPagamento.criarRascunhos.useMutation({
    onSuccess: (res: any) => {
      if (!res.criado) {
        setErrosRemessa(res.erros ?? []);
        toast.error("Nenhuma remessa foi criada. Corrija todas as pendências listadas.");
        return;
      }
      setErrosRemessa([]);
      setContasSelecionadasRemessa(new Set());
      toast.success(`${res.remessas.length} remessa(s) criada(s).`);
      utils.contasPagar.invalidate();
      utils.remessasPagamento.invalidate();
      window.location.assign("/bank-payments");
    },
    onError: (e) => toast.error(e.message),
  });

  const exportacao = trpc.contasPagar.exportar.useQuery({
    filtroEmitenteIds: emitenteIdsAplicados.size > 0 ? Array.from(emitenteIdsAplicados) : undefined,
    contaBancariaIds: contaBancariaIdsAplicados.size > 0 ? Array.from(contaBancariaIdsAplicados) : undefined,
    status: filtroStatus || undefined,
    tipoPagamento: filtroTipoPagamento || undefined,
    search: filtroSearch || undefined,
    dataVencimentoDe: filtroDataDe || undefined,
    dataVencimentoAte: filtroDataAte || undefined,
  }, { enabled: false });

  const alternarOrdenacao = (campo: string) => {
    if (sortBy === campo) setSortDir((direcao) => direcao === "asc" ? "desc" : "asc");
    else { setSortBy(campo); setSortDir("asc"); }
    setPage(1);
  };

  const handleExportExcel = async () => {
    const resultado = await exportacao.refetch();
    const linhas = resultado.data ?? [];
    if (!linhas.length) return;
    const ws = XLSX.utils.json_to_sheet(linhas.map((c: any) => ({
      ID: c.id,
      Descrição: c.descricao,
      Fornecedor: c.fornecedorNome || "",
      Emitente: c.emitenteNome || "",
      Banco: c.contaBancariaNome || "",
      Valor: c.valor,
      Vencimento: c.dataVencimento,
      Status: c.status,
      "Tipo Pagamento": c.tipoPagamento || "",
      Categoria: c.categoriaNome || "",
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contas a Pagar");
    XLSX.writeFile(wb, "contas_a_pagar.xlsx");
  };

  const contaElegivelParaRemessa = (conta: any) => !conta.cnabRemessaMotivo;
  const alternarContaRemessa = (id: number) => setContasSelecionadasRemessa((atual) => {
    const proximo = new Set(atual);
    if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
    return proximo;
  });
  const contasElegiveisDaPagina = (contas.data?.rows ?? []).filter(contaElegivelParaRemessa);
  const todasElegiveisDaPaginaSelecionadas = contasElegiveisDaPagina.length > 0 && contasElegiveisDaPagina.every((conta: any) => contasSelecionadasRemessa.has(Number(conta.id)));
  const alternarTodasContasDaPagina = () => setContasSelecionadasRemessa((atual) => {
    const proximo = new Set(atual);
    for (const conta of contasElegiveisDaPagina) {
      if (todasElegiveisDaPaginaSelecionadas) proximo.delete(Number(conta.id)); else proximo.add(Number(conta.id));
    }
    return proximo;
  });
  const criarRemessas = () => {
    if (contasSelecionadasRemessa.size === 0) return;
    setErrosRemessa([]);
    criarRascunhosRemessaMut.mutate({ contaPagarIds: Array.from(contasSelecionadasRemessa) });
  };

  const payableColumns: ReorderableColumn<any>[] = [
    { id: "selecionar", fixed: true, header: <Checkbox checked={todasElegiveisDaPaginaSelecionadas} onCheckedChange={alternarTodasContasDaPagina} aria-label="Selecionar contas elegíveis desta página" />, headerClassName: "p-2 text-center", className: "p-2 text-center", renderCell: (c) => <span title={c.cnabRemessaMotivo || undefined}><Checkbox checked={contasSelecionadasRemessa.has(Number(c.id))} disabled={!contaElegivelParaRemessa(c)} onCheckedChange={() => alternarContaRemessa(Number(c.id))} aria-label={`Selecionar conta ${c.id} para remessa`} /></span> },
    ...(colunaVisivel("id") ? [{ id: "id", header: "#", sortKey: "id", headerClassName: "p-2 text-left", className: "p-2", renderCell: (c: any) => c.id }] : []),
    ...(colunaVisivel("descricao") ? [{ id: "descricao", header: "Descrição", sortKey: "descricao", headerClassName: "p-2 text-left", className: "p-2 max-w-[200px] truncate", renderCell: (c: any) => c.descricao }] : []),
    ...(colunaVisivel("fornecedor") ? [{ id: "fornecedor", header: "Fornecedor", sortKey: "fornecedor", headerClassName: "p-2 text-left", className: "p-2 max-w-[150px] truncate", renderCell: (c: any) => c.fornecedorNome || "-" }] : []),
    ...(colunaVisivel("emitente") ? [{ id: "emitente", header: "Emitente", sortKey: "emitente", headerClassName: "p-2 text-left", className: "p-2 max-w-[180px] truncate", renderCell: (c: any) => c.emitenteNome || "-" }] : []),
    ...(colunaVisivel("banco") ? [{ id: "banco", header: "Banco", sortKey: "banco", headerClassName: "p-2 text-left", className: "p-2 max-w-[150px] truncate", renderCell: (c: any) => c.contaBancariaNome || "-" }] : []),
    ...(colunaVisivel("valor") ? [{ id: "valor", header: "Valor", sortKey: "valor", headerClassName: "p-2 text-right", className: "p-2 text-right font-mono", renderCell: (c: any) => `R$ ${Number(c.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` }] : []),
    ...(colunaVisivel("valorPago") ? [{ id: "valorPago", header: "Valor Pago", sortKey: "valorPago", headerClassName: "p-2 text-right", className: "p-2 text-right font-mono", renderCell: (c: any) => c.valorPago ? `R$ ${Number(c.valorPago).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "-" }] : []),
    ...(colunaVisivel("vencimento") ? [{ id: "vencimento", header: "Vencimento", sortKey: "vencimento", headerClassName: "p-2 text-center", className: "p-2 text-center", renderCell: (c: any) => c.dataVencimento ? new Date(c.dataVencimento + "T12:00:00").toLocaleDateString("pt-BR") : "-" }] : []),
    ...(colunaVisivel("ultimaAtualizacao") ? [{ id: "ultimaAtualizacao", header: "Última Atualização", sortKey: "ultimaAtualizacao", headerClassName: "p-2 text-center", className: "p-2 text-center whitespace-nowrap", renderCell: (c: any) => c.atualizadoEm ? new Date(c.atualizadoEm).toLocaleString("pt-BR") : "-" }] : []),
    ...(colunaVisivel("status") ? [{ id: "status", header: "Status", sortKey: "status", headerClassName: "p-2 text-center", className: "p-2 text-center", renderCell: (c: any) => <StatusBadge status={c.status} /> }] : []),
    ...(colunaVisivel("tipoPagamento") ? [{ id: "tipoPagamento", header: "Tipo Pgto", sortKey: "tipoPagamento", headerClassName: "p-2 text-center", className: "p-2 text-center text-xs", renderCell: (c: any) => c.tipoPagamento || "-" }] : []),
    ...(colunaVisivel("parcela") ? [{ id: "parcela", header: "Parcela", sortKey: "parcela", headerClassName: "p-2 text-center", className: "p-2 text-center text-xs", renderCell: (c: any) => c.totalParcelas ? `${c.parcelaAtual}/${c.totalParcelas}` : "-" }] : []),
    { id: "acoes", header: "Ações", headerClassName: "p-2 text-center", className: "p-2 text-center", renderCell: (c) => <><div className="flex gap-1 justify-center"><button className="text-blue-600 hover:underline text-xs" onClick={() => { setDetalhesId(c.id); setShowDetalhesModal(true); }}>Ver</button>{c.status !== "paga" && c.status !== "cancelada" && c.status !== "remessa_gerada" && <><button className="text-green-600 hover:underline text-xs" onClick={() => { setBaixaId(c.id); setShowBaixaModal(true); }}>Baixar</button><button className="text-gray-600 hover:underline text-xs" onClick={() => onAbrirModalLancamento(c.id)}>Editar</button><button className="text-orange-600 hover:underline text-xs" onClick={() => { const novaData = prompt("Nova data de vencimento (YYYY-MM-DD):"); if (novaData) duplicarMut.mutate({ id: c.id, novaDataVencimento: novaData }); }}>Duplicar</button><button className="text-red-600 hover:underline text-xs" onClick={() => { if (confirm("Cancelar esta conta?")) cancelarMut.mutate({ id: c.id }); }}>Cancelar</button></>}</div>{c.cnabRemessaMotivo && <p className="mt-1 text-left text-[11px] leading-tight text-red-600">Remessa: {c.cnabRemessaMotivo}</p>}</> },
  ];

  return (
    <div className="space-y-4">
      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-500">Pendente ({indicadores.data?.qtdPendente || 0})</p>
              <p className="text-xl font-bold">R$ {Number(indicadores.data?.totalPendente || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-sm text-gray-500">Vencido ({indicadores.data?.qtdVencido || 0})</p>
              <p className="text-xl font-bold text-red-600">R$ {Number(indicadores.data?.totalVencido || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-500">Pago este mês</p>
              <p className="text-xl font-bold text-green-600">R$ {Number(indicadores.data?.totalPagoMes || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e ações */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-xs">Buscar</Label>
          <Input className="w-48" placeholder="Descrição ou fornecedor..." value={filtroSearch} onChange={(e) => { setFiltroSearch(e.target.value); setPage(1); }} />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <select className="border rounded px-2 py-1.5 text-sm" value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            <option value="pendente">Pendente</option>
            <option value="vencida">Vencida</option>
            <option value="paga">Paga</option>
            <option value="aguardando_aprovacao">Aguardando Aprovação</option>
            <option value="cancelada">Cancelada</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="remessa_gerada">Remessa Gerada</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Tipo Pagamento</Label>
          <select className="border rounded px-2 py-1.5 text-sm" value={filtroTipoPagamento} onChange={(e) => { setFiltroTipoPagamento(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            <option value="boleto">Boleto</option>
            <option value="pix">PIX</option>
            <option value="transferencia">Transferência</option>
            <option value="cartao">Cartão</option>
            <option value="dinheiro">Dinheiro</option>
            <option value="cheque">Cheque</option>
            <option value="debito_automatico">Débito Automático</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Vencimento de</Label>
          <Input type="date" className="w-36" value={filtroDataDe} onChange={(e) => { setFiltroDataDe(e.target.value); setPage(1); }} />
        </div>
        <div>
          <Label className="text-xs">até</Label>
          <Input type="date" className="w-36" value={filtroDataAte} onChange={(e) => { setFiltroDataAte(e.target.value); setPage(1); }} />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">Emitentes{emitenteIdsAplicados.size > 0 ? ` (${emitenteIdsAplicados.size})` : ""}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 p-2">
            <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
              <Checkbox checked={todosEmitentesSelecionados} onCheckedChange={alternarTodosEmitentes} />
              <span className="text-sm font-medium">Selecionar todos</span>
            </label>
            <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
              {emitentesDisponiveis.map((emitente) => (
                <label key={emitente.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
                  <Checkbox checked={emitenteIdsTemporarios.has(Number(emitente.id))} onCheckedChange={() => alternarEmitenteTemporario(Number(emitente.id))} />
                  <span className="text-sm">{emitente.razaoSocial || emitente.nome || emitente.id}</span>
                </label>
              ))}
            </div>
            <Button size="sm" className="w-full" onClick={aplicarFiltroEmitentes}>Aplicar</Button>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">Bancos{contaBancariaIdsAplicados.size > 0 ? ` (${contaBancariaIdsAplicados.size})` : ""}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 p-2">
            <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
              <Checkbox checked={todosBancosSelecionados} onCheckedChange={alternarTodosBancos} />
              <span className="text-sm font-medium">Selecionar todos</span>
            </label>
            <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
              {bancosDisponiveis.map((banco) => (
                <label key={banco.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
                  <Checkbox checked={contaBancariaIdsTemporarios.has(Number(banco.id))} onCheckedChange={() => alternarBancoTemporario(Number(banco.id))} />
                  <span className="text-sm">{banco.nomeBanco || banco.id}</span>
                </label>
              ))}
            </div>
            <Button size="sm" className="w-full" onClick={aplicarFiltroBancos}>Aplicar</Button>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">Colunas</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-96 w-64 overflow-y-auto p-2">
            {COLUNAS_OPCIONAIS_CONTAS_PAGAR.map((coluna) => (
              <label key={coluna.chave} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
                <Checkbox checked={colunasVisiveis.has(coluna.chave)} onCheckedChange={() => alternarColuna(coluna.chave)} />
                <span className="text-sm">{coluna.label}</span>
              </label>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" onClick={handleExportExcel}><Download className="h-4 w-4 mr-1" />Excel</Button>
        <Button variant="outline" size="sm" onClick={() => gerarRecorrentesMut.mutate()} disabled={gerarRecorrentesMut.isPending}><Sparkles className="h-4 w-4 mr-1" />{gerarRecorrentesMut.isPending ? "Gerando..." : "Gerar Recorrentes"}</Button>
        <Button variant="outline" size="sm" onClick={criarRemessas} disabled={contasSelecionadasRemessa.size === 0 || criarRascunhosRemessaMut.isPending}>
          <FileText className="h-4 w-4 mr-1" />{criarRascunhosRemessaMut.isPending ? "Criando..." : `Criar remessa${contasSelecionadasRemessa.size === 1 ? "" : "s"}${contasSelecionadasRemessa.size > 0 ? ` (${contasSelecionadasRemessa.size})` : ""}`}
        </Button>
        <Button size="sm" onClick={() => onAbrirModalLancamento(null)}><Plus className="h-4 w-4 mr-1" />Novo Lançamento</Button>
      </div>

      {errosRemessa.length > 0 && (
        <div className="border border-red-300 bg-red-50 rounded p-3 text-sm text-red-800">
          <p className="font-medium">Nenhuma remessa foi criada. Corrija todas as linhas abaixo:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            {errosRemessa.map((erro) => <li key={erro.contaPagarId}>#{erro.contaPagarId} — {erro.descricao}: {erro.motivo}</li>)}
          </ul>
        </div>
      )}

      {/* Tabela */}
      <div className="border rounded overflow-x-auto">
        <ReorderableTable
          pageKey="accounts-payable-main"
          userId={userId}
          columns={payableColumns}
          rows={contas.data?.rows ?? []}
          getRowKey={(conta) => conta.id}
          getRowProps={() => ({ className: "border-t hover:bg-gray-50" })}
          sort={{ column: sortBy, direction: sortDir, onToggle: alternarOrdenacao }}
          tableClassName="w-full text-sm"
          headClassName="bg-gray-50"
          emptyMessage="Nenhuma conta encontrada"
          emptyClassName="p-4 text-center text-gray-400"
        />
      </div>

      {/* Paginação */}
      {contas.data && contas.data.totalPages > 1 && (
        <div className="flex gap-2 items-center justify-center">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="text-sm">Página {page} de {contas.data.totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= contas.data.totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
        </div>
      )}

      {/* Modais */}
      {showBaixaModal && baixaId && <ModalBaixa id={baixaId} onClose={() => setShowBaixaModal(false)} />}
      {showDetalhesModal && detalhesId && <ModalDetalhes id={detalhesId} onClose={() => setShowDetalhesModal(false)} />}
    </div>
  );
}

// ============================================================
// STATUS BADGE
// ============================================================
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    pendente: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pendente" },
    vencida: { bg: "bg-red-100", text: "text-red-800", label: "Vencida" },
    paga: { bg: "bg-green-100", text: "text-green-800", label: "Paga" },
    aguardando_aprovacao: { bg: "bg-blue-100", text: "text-blue-800", label: "Aguardando" },
    cancelada: { bg: "bg-gray-100", text: "text-gray-800", label: "Cancelada" },
    em_andamento: { bg: "bg-orange-100", text: "text-orange-800", label: "Em Andamento" },
    remessa_gerada: { bg: "bg-purple-100", text: "text-purple-800", label: "Remessa Gerada" },
  };
  const s = map[status] || { bg: "bg-gray-100", text: "text-gray-800", label: status };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
}

// ============================================================
// MODAL LANÇAMENTO (criar/editar)
// ============================================================
function ModalLancamento({ id, onClose }: { id: number | null; onClose: () => void }) {
  const utils = trpc.useUtils();
  const existing = trpc.contasPagar.getById.useQuery({ id: id! }, { enabled: !!id });
  const categorias = trpc.contasPagar.categorias.list.useQuery();
  const fornecedores = trpc.contasPagar.fornecedores.list.useQuery();
  const centrosCusto = trpc.contasPagar.centrosCusto.list.useQuery();
  const contasBancarias = trpc.bankAccounts.list.useQuery();
  const emitentes = trpc.emitentes.list.useQuery();

  const [form, setForm] = useState<any>({});
  const [parcelar, setParcelar] = useState(false);
  const [numParcelas, setNumParcelas] = useState(2);
  const [intervaloDias, setIntervaloDias] = useState(30);
  const [recorrente, setRecorrente] = useState(false);

  // Preencher form uma única vez ao abrir cada lançamento
  const formInicializadoParaId = useRef<number | null>(null);
  useEffect(() => {
    if (id && existing.data && formInicializadoParaId.current !== id) {
      setForm(existing.data);
      setRecorrente(!!existing.data.recorrente);
      formInicializadoParaId.current = id;
    }
  }, [existing.data, id]);

  const createMut = trpc.contasPagar.create.useMutation({
    onSuccess: () => { toast.success("Lançamento criado"); utils.contasPagar.invalidate(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.contasPagar.update.useMutation({
    onSuccess: () => { toast.success("Lançamento atualizado"); utils.contasPagar.invalidate(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = () => {
    if (!form.emitenteId) {
      toast.error("Selecione o emitente");
      return;
    }

    const base = {
      descricao: form.descricao || "",
      emitenteId: Number(form.emitenteId),
      valor: Number(form.valor) || 0,
      dataVencimento: form.dataVencimento || "",
      dataEmissao: form.dataEmissao || null,
      mesCompetencia: form.mesCompetencia || null,
      fornecedorId: form.fornecedorId ? Number(form.fornecedorId) : null,
      categoriaId: form.categoriaId ? Number(form.categoriaId) : null,
      centroCusto: form.centroCusto || null,
      centroCustoId: form.centroCustoId ? Number(form.centroCustoId) : null,
      tipoPagamento: form.tipoPagamento || null,
      dadoPagamento: form.dadoPagamento || null,
      chaveAcesso: form.chaveAcesso || null,
      contaBancariaId: form.contaBancariaId ? Number(form.contaBancariaId) : null,
      observacoes: form.observacoes || null,
      recorrente: recorrente,
      recorrenciaFrequencia: recorrente ? (form.recorrenciaFrequencia || "mensal") : null,
      recorrenciaDia: recorrente ? (Number(form.recorrenciaDia) || null) : null,
      status: form.status || undefined,
    };

    if (id) {
      updateMut.mutate({ id, ...base });
    } else if (parcelar && numParcelas > 1) {
      // Criar múltiplas parcelas
      const agrupadorId = crypto.randomUUID();
      const valorParcela = Math.round((base.valor / numParcelas) * 100) / 100;
      for (let i = 0; i < numParcelas; i++) {
        const venc = new Date(base.dataVencimento + "T12:00:00");
        venc.setDate(venc.getDate() + (i * intervaloDias));
        createMut.mutate({
          ...base,
          valor: valorParcela,
          dataVencimento: venc.toISOString().split("T")[0],
          parcelaAtual: i + 1,
          totalParcelas: numParcelas,
          agrupadorId,
        });
      }
    } else {
      createMut.mutate({
        ...base,
        agrupadorId: recorrente ? crypto.randomUUID() : undefined,
      });
    }
  };

  // Upload XML para preencher campos automaticamente
  const handleXmlUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      try {
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, "text/xml");
        // Extrair chave de acesso
        const infNFe = xml.querySelector("infNFe");
        if (infNFe) {
          const chave = infNFe.getAttribute("Id")?.replace("NFe", "") || "";
          setForm((f: any) => ({ ...f, chaveAcesso: chave }));
        }
        // Extrair valor total
        const vProd = xml.querySelector("total ICMSTot vNF");
        if (vProd) {
          setForm((f: any) => ({ ...f, valor: Number(vProd.textContent) || f.valor }));
        }
        // Extrair CNPJ do emitente da NFe (fornecedor)
        const cnpjEmit = xml.querySelector("emit CNPJ");
        if (cnpjEmit) {
          const cnpj = cnpjEmit.textContent || "";
          // Tentar encontrar fornecedor pelo CNPJ
          const forn = (fornecedores.data as any)?.find?.((d: any) => d.cnpj?.replace(/\D/g, "") === cnpj.replace(/\D/g, ""));
          if (forn) {
            setForm((f: any) => ({ ...f, fornecedorId: forn.id }));
          }
        }
        // Extrair data emissão
        const dhEmi = xml.querySelector("ide dhEmi");
        if (dhEmi) {
          const dt = dhEmi.textContent?.substring(0, 10) || "";
          setForm((f: any) => ({ ...f, dataEmissao: dt }));
        }
        toast.success("XML importado com sucesso");
      } catch {
        toast.error("Erro ao parsear XML");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">{id ? "Editar Lançamento" : "Novo Lançamento"}</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Seção 1: Dados Básicos */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-medium px-1">Dados Básicos</legend>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Descrição *</Label>
                <Input value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              </div>
              <div>
                <Label>Emitente *</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.emitenteId || ""} onChange={(e) => setForm({ ...form, emitenteId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {(emitentes.data as any)?.map?.((emitente: any) => (
                    <option key={emitente.id} value={emitente.id}>{emitente.razaoSocial}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Fornecedor</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.fornecedorId || ""} onChange={(e) => setForm({ ...form, fornecedorId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {(fornecedores.data as any)?.map?.((d: any) => (
                    <option key={d.id} value={d.id}>{d.razaoSocial}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Categoria</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.categoriaId || ""} onChange={(e) => setForm({ ...form, categoriaId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {categorias.data?.map?.((c: any) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" value={form.valor || ""} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
              </div>
              <div>
                <Label>Data Vencimento *</Label>
                <Input type="date" value={form.dataVencimento || ""} onChange={(e) => setForm({ ...form, dataVencimento: e.target.value })} />
              </div>
              <div>
                <Label>Data Emissão</Label>
                <Input type="date" value={form.dataEmissao || ""} onChange={(e) => setForm({ ...form, dataEmissao: e.target.value })} />
              </div>
              <div>
                <Label>Mês Competência</Label>
                <Input type="month" value={form.mesCompetencia || ""} onChange={(e) => setForm({ ...form, mesCompetencia: e.target.value })} />
              </div>
              <div>
                <Label>Centro de Custo</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.centroCustoId || ""} onChange={(e) => setForm({ ...form, centroCustoId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {centrosCusto.data?.map?.((c: any) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.status || "pendente"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="pendente">Pendente</option>
                  <option value="aguardando_aprovacao">Aguardando Aprovação</option>
                  <option value="em_andamento">Em Andamento</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* Seção 2: Pagamento */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-medium px-1">Pagamento</legend>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de Pagamento</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.tipoPagamento || ""} onChange={(e) => setForm({ ...form, tipoPagamento: e.target.value })}>
                  <option value="">Selecione...</option>
                  <option value="boleto">Boleto</option>
                  <option value="pix">PIX</option>
                  <option value="transferencia">Transferência</option>
                  <option value="cartao">Cartão</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="cheque">Cheque</option>
                  <option value="debito_automatico">Débito Automático</option>
                </select>
              </div>
              <div>
                <Label>Conta Bancária</Label>
                <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.contaBancariaId || ""} onChange={(e) => setForm({ ...form, contaBancariaId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {(contasBancarias.data as any)?.map?.((cb: any) => (
                    <option key={cb.id} value={cb.id}>{cb.banco} - Ag {cb.agencia} / CC {cb.conta}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Dado de Pagamento {form.tipoPagamento === "boleto" ? "(Linha Digitável)" : form.tipoPagamento === "pix" ? "(Copia e Cola PIX)" : ""}</Label>
                <Input value={form.dadoPagamento || ""} onChange={(e) => setForm({ ...form, dadoPagamento: e.target.value })} placeholder={form.tipoPagamento === "boleto" ? "00000.00000 00000.000000 00000.000000 0 00000000000000" : form.tipoPagamento === "pix" ? "Cole o código PIX aqui..." : ""} />
              </div>
            </div>
          </fieldset>

          {/* Seção 3: Nota Fiscal */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-medium px-1">Nota Fiscal</legend>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Chave de Acesso (44 dígitos)</Label>
                  <Input value={form.chaveAcesso || ""} onChange={(e) => setForm({ ...form, chaveAcesso: e.target.value })} maxLength={44} />
                </div>
                {form.chaveAcesso && form.chaveAcesso.length === 44 && (
                  <a href={`https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConteudo=7PhJ+gAVw2g=&tipoConsulta=completa&nfe=${form.chaveAcesso}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs whitespace-nowrap pb-2">Consultar SEFAZ</a>
                )}
              </div>
              <div>
                <Label>Importar XML da NFe</Label>
                <Input type="file" accept=".xml" onChange={handleXmlUpload} />
              </div>
            </div>
          </fieldset>

          {/* Seção 4: Parcelamento (só no criar) */}
          {!id && (
            <fieldset className="border rounded p-3 space-y-3">
              <legend className="text-sm font-medium px-1">Parcelamento</legend>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={parcelar} onChange={(e) => setParcelar(e.target.checked)} id="chk-parcelar" />
                <label htmlFor="chk-parcelar" className="text-sm">Parcelar este lançamento</label>
              </div>
              {parcelar && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Número de Parcelas</Label>
                    <Input type="number" min={2} value={numParcelas} onChange={(e) => setNumParcelas(Number(e.target.value))} />
                  </div>
                  <div>
                    <Label>Intervalo (dias)</Label>
                    <Input type="number" min={1} value={intervaloDias} onChange={(e) => setIntervaloDias(Number(e.target.value))} />
                  </div>
                  {numParcelas > 1 && form.valor && form.dataVencimento && (
                    <div className="col-span-2 text-xs text-gray-500">
                      {Array.from({ length: numParcelas }, (_, i) => {
                        const venc = new Date(form.dataVencimento + "T12:00:00");
                        venc.setDate(venc.getDate() + (i * intervaloDias));
                        return `${i + 1}/${numParcelas}: R$ ${(Number(form.valor) / numParcelas).toFixed(2)} em ${venc.toLocaleDateString("pt-BR")}`;
                      }).join(" | ")}
                    </div>
                  )}
                </div>
              )}
            </fieldset>
          )}

          {/* Seção 5: Recorrência */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-medium px-1">Recorrência</legend>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={recorrente} onChange={(e) => setRecorrente(e.target.checked)} id="chk-recorrente" />
              <label htmlFor="chk-recorrente" className="text-sm">Conta recorrente (gera automaticamente)</label>
            </div>
            {recorrente && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Frequência</Label>
                  <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.recorrenciaFrequencia || "mensal"} onChange={(e) => setForm({ ...form, recorrenciaFrequencia: e.target.value })}>
                    <option value="mensal">Mensal</option>
                    <option value="quinzenal">Quinzenal</option>
                    <option value="semanal">Semanal</option>
                  </select>
                </div>
                <div>
                  <Label>Dia do Vencimento</Label>
                  <Input type="number" min={1} max={31} value={form.recorrenciaDia || ""} onChange={(e) => setForm({ ...form, recorrenciaDia: e.target.value })} />
                </div>
              </div>
            )}
          </fieldset>

          {/* Observações */}
          <div>
            <Label>Observações</Label>
            <textarea className="border rounded px-2 py-1.5 text-sm w-full h-20 resize-none" value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending}>
            {id ? "Salvar" : "Criar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODAL BAIXA
// ============================================================
function ModalBaixa({ id, onClose }: { id: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const conta = trpc.contasPagar.getById.useQuery({ id });
  const contasBancarias = trpc.bankAccounts.list.useQuery();
  const arquivos = trpc.contasPagar.arquivos.list.useQuery({ contaPagarId: id });
  const [form, setForm] = useState<any>({
    dataPagamento: new Date().toISOString().split("T")[0],
    valorPago: 0,
    juros: 0,
    multa: 0,
    desconto: 0,
    contaBancariaId: null,
    observacoes: "",
  });

  useMemo(() => {
    if (conta.data) {
      setForm((f: any) => ({ ...f, valorPago: conta.data.valor, contaBancariaId: conta.data.contaBancariaId }));
    }
  }, [conta.data]);

  const uploadArquivoBaixaMut = trpc.contasPagar.arquivos.upload.useMutation({
    onError: (e) => toast.error(e.message),
  });
  const addArquivoBaixaMut = trpc.contasPagar.arquivos.add.useMutation({
    onSuccess: () => {
      toast.success("Arquivo anexado");
      utils.contasPagar.arquivos.list.invalidate({ contaPagarId: id });
    },
    onError: (e) => toast.error(e.message),
  });
  const deleteArquivoBaixaMut = trpc.contasPagar.arquivos.delete.useMutation({
    onSuccess: () => {
      toast.success("Arquivo removido");
      utils.contasPagar.arquivos.list.invalidate({ contaPagarId: id });
    },
    onError: (e) => toast.error(e.message),
  });

  const handleFileUploadBaixa = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const fileBase64 = ev.target?.result as string;
      const tipo = file.name.endsWith(".xml") ? "xml" : file.name.endsWith(".pdf") ? "pdf" : "comprovante";
      const result = await uploadArquivoBaixaMut.mutateAsync({ fileBase64, fileName: file.name });
      await addArquivoBaixaMut.mutateAsync({ contaPagarId: id, nome: file.name, url: result.path, tipo });
    };
    reader.readAsDataURL(file);
  };

  const baixarMut = trpc.contasPagar.baixar.useMutation({
    onSuccess: () => { toast.success("Baixa realizada"); utils.contasPagar.invalidate(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = () => {
    baixarMut.mutate({
      id,
      dataPagamento: form.dataPagamento,
      valorPago: Number(conta.data?.valor || 0) + Number(form.juros || 0) + Number(form.multa || 0) - Number(form.desconto || 0),
      juros: Number(form.juros) || null,
      multa: Number(form.multa) || null,
      desconto: Number(form.desconto) || null,
      contaBancariaId: form.contaBancariaId ? Number(form.contaBancariaId) : null,
      observacoes: form.observacoes || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Dar Baixa</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {conta.data && (
            <div className="bg-gray-50 rounded p-2 text-sm">
              <p><strong>{conta.data.descricao}</strong></p>
              <p>Valor: R$ {Number(conta.data.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              <p>Vencimento: {conta.data.dataVencimento ? new Date(conta.data.dataVencimento + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</p>
            </div>
          )}
          <div>
            <Label>Data do Pagamento *</Label>
            <Input type="date" value={form.dataPagamento} onChange={(e) => setForm({ ...form, dataPagamento: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Juros</Label>
              <Input type="number" step="0.01" value={form.juros} onChange={(e) => setForm({ ...form, juros: e.target.value })} />
            </div>
            <div>
              <Label>Multa</Label>
              <Input type="number" step="0.01" value={form.multa} onChange={(e) => setForm({ ...form, multa: e.target.value })} />
            </div>
            <div>
              <Label>Desconto</Label>
              <Input type="number" step="0.01" value={form.desconto} onChange={(e) => setForm({ ...form, desconto: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Valor Pago (calculado)</Label>
            <Input type="text" readOnly value={`R$ ${(Number(conta.data?.valor || 0) + Number(form.juros || 0) + Number(form.multa || 0) - Number(form.desconto || 0)).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} className="bg-gray-100 cursor-not-allowed" />
          </div>
          <div>
            <Label>Conta Bancária</Label>
            <select className="border rounded px-2 py-1.5 text-sm w-full" value={form.contaBancariaId || ""} onChange={(e) => setForm({ ...form, contaBancariaId: e.target.value })}>
              <option value="">Selecione...</option>
              {(contasBancarias.data as any)?.map?.((cb: any) => (
                <option key={cb.id} value={cb.id}>{cb.banco} - Ag {cb.agencia} / CC {cb.conta}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Observação</Label>
            <textarea className="border rounded px-2 py-1.5 text-sm w-full h-16 resize-none" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
          <div>
            <Label>Comprovante / Arquivo</Label>
            <label className="cursor-pointer flex items-center gap-2 border rounded px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 w-fit">
              <Upload className="h-4 w-4" /> Anexar arquivo
              <input type="file" className="hidden" onChange={handleFileUploadBaixa} disabled={uploadArquivoBaixaMut.isPending} />
            </label>
            {uploadArquivoBaixaMut.isPending && <p className="text-xs text-gray-500 mt-1">Enviando...</p>}
            {arquivos.data?.map?.((arq: any) => (
              <div key={arq.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1 mt-2">
                <a href={arq.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{arq.nome}</a>
                <button onClick={() => { if (confirm("Remover arquivo?")) deleteArquivoBaixaMut.mutate({ id: arq.id }); }} className="text-red-500" title="Remover arquivo"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
            {(!arquivos.data?.length) && <p className="text-xs text-gray-400 mt-2">Nenhum arquivo anexado</p>}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={baixarMut.isPending}>Confirmar Baixa</Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODAL DETALHES
// ============================================================
function ModalDetalhes({ id, onClose }: { id: number; onClose: () => void }) {
  const conta = trpc.contasPagar.getById.useQuery({ id });
  const historico = trpc.contasPagar.historico.useQuery({ contaPagarId: id });
  const arquivos = trpc.contasPagar.arquivos.list.useQuery({ contaPagarId: id });
  const utils = trpc.useUtils();
  const addArquivoMut = trpc.contasPagar.arquivos.add.useMutation({
    onSuccess: () => { toast.success("Arquivo adicionado"); utils.contasPagar.arquivos.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteArquivoMut = trpc.contasPagar.arquivos.delete.useMutation({
    onSuccess: () => { toast.success("Arquivo removido"); utils.contasPagar.arquivos.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const uploadArquivoMut = trpc.contasPagar.arquivos.upload.useMutation({
    onError: (e) => toast.error(e.message),
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const fileBase64 = ev.target?.result as string;
      const tipo = file.name.endsWith(".xml") ? "xml" : file.name.endsWith(".pdf") ? "pdf" : "comprovante";
      const result = await uploadArquivoMut.mutateAsync({ fileBase64, fileName: file.name });
      addArquivoMut.mutate({ contaPagarId: id, nome: file.name, url: result.path, tipo });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Detalhes da Conta #{id}</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {conta.data && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><strong>Descrição:</strong> {conta.data.descricao}</div>
                <div><strong>Fornecedor:</strong> {conta.data.fornecedorNome || "-"}</div>
                <div><strong>Valor:</strong> R$ {Number(conta.data.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
                <div><strong>Status:</strong> <StatusBadge status={conta.data.status} /></div>
                <div><strong>Vencimento:</strong> {conta.data.dataVencimento ? new Date(conta.data.dataVencimento + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</div>
                <div><strong>Emissão:</strong> {conta.data.dataEmissao ? new Date(conta.data.dataEmissao + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</div>
                <div><strong>Tipo Pagamento:</strong> {conta.data.tipoPagamento || "-"}</div>
                <div><strong>Dado Pagamento:</strong> {conta.data.dadoPagamento || "-"}</div>
                <div><strong>Categoria:</strong> {conta.data.categoriaNome || "-"}</div>
                <div><strong>Centro de Custo:</strong> {conta.data.centroCustoNome || conta.data.centroCusto || "-"}</div>
                <div><strong>Conta Bancária:</strong> {conta.data.contaBancariaNome || "-"}</div>
                <div><strong>Parcela:</strong> {conta.data.totalParcelas ? `${conta.data.parcelaAtual}/${conta.data.totalParcelas}` : "-"}</div>
                {conta.data.chaveAcesso && (
                  <div className="col-span-2">
                    <strong>Chave de Acesso:</strong>{" "}
                    <a href={`https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConteudo=7PhJ+gAVw2g=&tipoConsulta=completa&nfe=${conta.data.chaveAcesso}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">{conta.data.chaveAcesso}</a>
                  </div>
                )}
                {conta.data.dataPagamento && (
                  <>
                    <div><strong>Data Pagamento:</strong> {new Date(conta.data.dataPagamento + "T12:00:00").toLocaleDateString("pt-BR")}</div>
                    <div><strong>Valor Pago:</strong> R$ {Number(conta.data.valorPago || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
                    <div><strong>Juros:</strong> R$ {Number(conta.data.juros || 0).toFixed(2)}</div>
                    <div><strong>Multa:</strong> R$ {Number(conta.data.multa || 0).toFixed(2)}</div>
                    <div><strong>Desconto:</strong> R$ {Number(conta.data.desconto || 0).toFixed(2)}</div>
                  </>
                )}
                {conta.data.observacoes && (
                  <div className="col-span-2"><strong>Observações:</strong> {conta.data.observacoes}</div>
                )}
              </div>

              {/* PIX QR Code */}
              {conta.data.tipoPagamento === "pix" && conta.data.dadoPagamento && (
                <div className="border rounded p-3">
                  <p className="text-sm font-medium mb-2">QR Code PIX</p>
                  <div className="flex justify-center">
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(conta.data.dadoPagamento)}`} alt="QR Code PIX" className="w-48 h-48" />
                  </div>
                  <p className="text-xs text-center text-gray-500 mt-1 break-all">{conta.data.dadoPagamento}</p>
                </div>
              )}

              {/* Código de Barras Boleto */}
              {conta.data.tipoPagamento === "boleto" && conta.data.dadoPagamento && (
                <BoletoBarcode linhaDigitavel={conta.data.dadoPagamento} />
              )}
            </>
          )}

          {/* Arquivos */}
          <div className="border rounded p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Arquivos</p>
              <label className="cursor-pointer text-blue-600 text-xs hover:underline flex items-center gap-1">
                <Upload className="h-3 w-3" /> Anexar
                <input type="file" className="hidden" onChange={handleFileUpload} />
              </label>
            </div>
            {arquivos.data?.map?.((arq: any) => (
              <div key={arq.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
                <a href={arq.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{arq.nome}</a>
                <button onClick={() => { if (confirm("Remover arquivo?")) deleteArquivoMut.mutate({ id: arq.id }); }} className="text-red-500"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
            {(!arquivos.data?.length) && <p className="text-xs text-gray-400">Nenhum arquivo anexado</p>}
          </div>

          {/* Histórico */}
          <div className="border rounded p-3 space-y-2">
            <p className="text-sm font-medium">Histórico de Alterações</p>
            {historico.data?.map?.((h: any) => (
              <div key={h.id} className="text-xs text-gray-600 flex gap-2">
                <span className="text-gray-400">{new Date(h.criadoEm).toLocaleString("pt-BR")}</span>
                <span>{h.userName || "Sistema"}: {h.statusAnterior} → {h.statusNovo}</span>
                {h.observacao && <span className="text-gray-400">({h.observacao})</span>}
              </div>
            ))}
            {(!historico.data?.length) && <p className="text-xs text-gray-400">Nenhum registro</p>}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ABA RECORRENTES
// ============================================================
function AbaRecorrentes() {
  const [search, setSearch] = useState("");
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("vencimento");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const utils = trpc.useUtils();
  const recorrentes = trpc.contasPagar.recorrentes.list.useQuery({ search: search || undefined, sortBy: sortBy as any, sortDir });
  const exportacao = trpc.contasPagar.recorrentes.exportar.useQuery({ search: search || undefined }, { enabled: false });
  const excluirMut = trpc.contasPagar.recorrentes.excluirSelecionadas.useMutation({
    onSuccess: () => {
      toast.success(`${selecionadas.length} recorrência(s) excluída(s)`);
      setSelecionadas([]);
      utils.contasPagar.recorrentes.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  useEffect(() => { setSelecionadas([]); }, [search]);

  const series = recorrentes.data ?? [];
  const todasSelecionadas = series.length > 0 && series.every((serie: any) => selecionadas.includes(serie.agrupadorId));
  const toggleSerie = (agrupadorId: string) => {
    setSelecionadas((atual) => atual.includes(agrupadorId)
      ? atual.filter((id) => id !== agrupadorId)
      : [...atual, agrupadorId]
    );
  };
  const toggleTodas = () => {
    setSelecionadas(todasSelecionadas ? [] : series.map((serie: any) => serie.agrupadorId));
  };
  const alternarOrdenacao = (campo: string) => {
    if (sortBy === campo) setSortDir((direcao) => direcao === "asc" ? "desc" : "asc");
    else { setSortBy(campo); setSortDir("asc"); }
  };

  const handleExcluirSelecionadas = () => {
    if (!selecionadas.length) return;
    if (confirm(`Excluir ${selecionadas.length} recorrência(s)? Os lançamentos existentes serão preservados, mas não serão gerados novos lançamentos.`)) {
      excluirMut.mutate({ agrupadorIds: selecionadas });
    }
  };
  const handleExportExcel = async () => {
    const resultado = await exportacao.refetch();
    if (resultado.error) {
      toast.error(resultado.error.message);
      return;
    }
    const linhas = resultado.data ?? [];
    if (!linhas.length) {
      toast.error("Nenhuma recorrência encontrada para exportar.");
      return;
    }
    const frequencias: Record<string, string> = { semanal: "Semanal", quinzenal: "Quinzenal", mensal: "Mensal" };
    const ws = XLSX.utils.json_to_sheet(linhas.map((serie: any) => ({
      Descrição: serie.descricao,
      Fornecedor: serie.fornecedorNome || "",
      Valor: serie.valor,
      Frequência: frequencias[serie.recorrenciaFrequencia] || serie.recorrenciaFrequencia,
      Dia: serie.recorrenciaFrequencia === "mensal" ? serie.recorrenciaDia : "",
      "Vencimento Atual": serie.dataVencimento,
      Status: serie.status,
      Lançamentos: serie.totalLancamentos,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recorrentes");
    XLSX.writeFile(wb, "contas_a_pagar_recorrentes.xlsx");
  };
  const frequencias: Record<string, string> = { semanal: "Semanal", quinzenal: "Quinzenal", mensal: "Mensal" };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-xs">Buscar</Label>
          <Input className="w-64" placeholder="Descrição ou fornecedor..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exportacao.isFetching}><Download className="h-4 w-4 mr-1" />{exportacao.isFetching ? "Exportando..." : "Excel"}</Button>
        <Button variant="destructive" size="sm" onClick={handleExcluirSelecionadas} disabled={!selecionadas.length || excluirMut.isPending}><Trash2 className="h-4 w-4 mr-1" />{excluirMut.isPending ? "Excluindo..." : `Excluir recorrências selecionadas (${selecionadas.length})`}</Button>
      </div>

      {recorrentes.isLoading ? (
        <div className="border rounded p-4 text-center text-gray-400">Carregando recorrências...</div>
      ) : recorrentes.error ? (
        <div className="border rounded p-4 text-center text-red-600">{recorrentes.error.message}</div>
      ) : (
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-center"><input type="checkbox" checked={todasSelecionadas} onChange={toggleTodas} aria-label="Selecionar todas as recorrências filtradas" /></th>
                <th className="p-2 text-left"><SortHeader label="Descrição" ativo={sortBy === "descricao"} direcao={sortDir} onClick={() => alternarOrdenacao("descricao")} /></th>
                <th className="p-2 text-left"><SortHeader label="Fornecedor" ativo={sortBy === "fornecedor"} direcao={sortDir} onClick={() => alternarOrdenacao("fornecedor")} /></th>
                <th className="p-2 text-right"><SortHeader label="Valor" ativo={sortBy === "valor"} direcao={sortDir} onClick={() => alternarOrdenacao("valor")} /></th>
                <th className="p-2 text-center"><SortHeader label="Frequência" ativo={sortBy === "frequencia"} direcao={sortDir} onClick={() => alternarOrdenacao("frequencia")} /></th>
                <th className="p-2 text-center"><SortHeader label="Dia" ativo={sortBy === "dia"} direcao={sortDir} onClick={() => alternarOrdenacao("dia")} /></th>
                <th className="p-2 text-center"><SortHeader label="Vencimento atual" ativo={sortBy === "vencimento"} direcao={sortDir} onClick={() => alternarOrdenacao("vencimento")} /></th>
                <th className="p-2 text-center"><SortHeader label="Status" ativo={sortBy === "status"} direcao={sortDir} onClick={() => alternarOrdenacao("status")} /></th>
                <th className="p-2 text-center"><SortHeader label="Lançamentos" ativo={sortBy === "lancamentos"} direcao={sortDir} onClick={() => alternarOrdenacao("lancamentos")} /></th>
              </tr>
            </thead>
            <tbody>
              {series.map((serie: any) => (
                <tr key={serie.agrupadorId} className="border-t hover:bg-gray-50">
                  <td className="p-2 text-center"><input type="checkbox" checked={selecionadas.includes(serie.agrupadorId)} onChange={() => toggleSerie(serie.agrupadorId)} aria-label={`Selecionar ${serie.descricao}`} /></td>
                  <td className="p-2">{serie.descricao}</td>
                  <td className="p-2">{serie.fornecedorNome || "-"}</td>
                  <td className="p-2 text-right font-mono">R$ {Number(serie.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  <td className="p-2 text-center">{frequencias[serie.recorrenciaFrequencia] || serie.recorrenciaFrequencia}</td>
                  <td className="p-2 text-center">{serie.recorrenciaFrequencia === "mensal" ? (serie.recorrenciaDia || "-") : "-"}</td>
                  <td className="p-2 text-center">{serie.dataVencimento ? new Date(serie.dataVencimento + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</td>
                  <td className="p-2 text-center"><StatusBadge status={serie.status} /></td>
                  <td className="p-2 text-center">{serie.totalLancamentos}</td>
                </tr>
              ))}
              {!series.length && <tr><td colSpan={9} className="p-4 text-center text-gray-400">Nenhuma recorrência encontrada.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ABA CATEGORIAS
// ============================================================
function AbaCategorias() {
  const categorias = trpc.contasPagar.categorias.list.useQuery();
  const utils = trpc.useUtils();
  const [novaCategoria, setNovaCategoria] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editNome, setEditNome] = useState("");

  const createMut = trpc.contasPagar.categorias.create.useMutation({
    onSuccess: () => { toast.success("Categoria criada"); setNovaCategoria(""); utils.contasPagar.categorias.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.contasPagar.categorias.update.useMutation({
    onSuccess: () => { toast.success("Categoria atualizada"); setEditId(null); utils.contasPagar.categorias.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMut = trpc.contasPagar.categorias.delete.useMutation({
    onSuccess: () => { toast.success("Categoria removida"); utils.contasPagar.categorias.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4 max-w-lg">
      <div className="flex gap-2">
        <Input placeholder="Nova categoria..." value={novaCategoria} onChange={(e) => setNovaCategoria(e.target.value)} />
        <Button size="sm" onClick={() => { if (novaCategoria.trim()) createMut.mutate({ nome: novaCategoria.trim() }); }} disabled={createMut.isPending}>
          <Plus className="h-4 w-4 mr-1" />Criar
        </Button>
      </div>
      <div className="border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left">Nome</th>
              <th className="p-2 text-center w-32">Ações</th>
            </tr>
          </thead>
          <tbody>
            {categorias.data?.map?.((c: any) => (
              <tr key={c.id} className="border-t">
                <td className="p-2">
                  {editId === c.id ? (
                    <Input value={editNome} onChange={(e) => setEditNome(e.target.value)} className="h-7" />
                  ) : (
                    c.nome
                  )}
                </td>
                <td className="p-2 text-center">
                  {editId === c.id ? (
                    <div className="flex gap-1 justify-center">
                      <button className="text-green-600 text-xs hover:underline" onClick={() => updateMut.mutate({ id: c.id, nome: editNome })}>Salvar</button>
                      <button className="text-gray-600 text-xs hover:underline" onClick={() => setEditId(null)}>Cancelar</button>
                    </div>
                  ) : (
                    <div className="flex gap-1 justify-center">
                      <button className="text-blue-600 text-xs hover:underline" onClick={() => { setEditId(c.id); setEditNome(c.nome); }}>Editar</button>
                      <button className="text-red-600 text-xs hover:underline" onClick={() => { if (confirm("Remover categoria?")) deleteMut.mutate({ id: c.id }); }}>Excluir</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {(!categorias.data?.length) && (
              <tr><td colSpan={2} className="p-4 text-center text-gray-400">Nenhuma categoria cadastrada</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// ABA RELATÓRIOS
// ============================================================
function AbaRelatorios() {
  const [tipo, setTipo] = useState<"fluxo" | "aging" | "fornecedor" | "categoria">("fluxo");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [tipoGrafico, setTipoGrafico] = useState<string>(() => {
    try { return localStorage.getItem("cp_chart_type_" + tipo) || "bar"; } catch { return "bar"; }
  });

  const relatorio = trpc.contasPagar.relatorio.useQuery({
    tipo,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
  });

  const handleTipoGraficoChange = (novoTipo: string) => {
    setTipoGrafico(novoTipo);
    try { localStorage.setItem("cp_chart_type_" + tipo, novoTipo); } catch {}
  };

  // Quando muda o tipo de relatório, carregar preferência salva
  useMemo(() => {
    try {
      const saved = localStorage.getItem("cp_chart_type_" + tipo);
      if (saved) setTipoGrafico(saved);
      else setTipoGrafico("bar");
    } catch {}
  }, [tipo]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Tipo de Relatório</Label>
          <select className="border rounded px-2 py-1.5 text-sm" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
            <option value="fluxo">Fluxo de Pagamentos</option>
            <option value="aging">Aging (Vencimento)</option>
            <option value="fornecedor">Por Fornecedor</option>
            <option value="categoria">Por Categoria</option>
          </select>
        </div>
        {tipo !== "aging" && (
          <>
            <div>
              <Label className="text-xs">De</Label>
              <Input type="date" className="w-36" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Até</Label>
              <Input type="date" className="w-36" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </>
        )}
        <div>
          <Label className="text-xs">Tipo de Gráfico</Label>
          <select className="border rounded px-2 py-1.5 text-sm" value={tipoGrafico} onChange={(e) => handleTipoGraficoChange(e.target.value)}>
            <option value="bar">Colunas</option>
            <option value="line">Linhas</option>
            <option value="pie">Pizza</option>
          </select>
        </div>
      </div>

      {/* Resultado */}
      <Card>
        <CardContent className="p-4">
          {relatorio.isLoading && <p className="text-gray-400 text-sm">Carregando...</p>}

          {tipo === "aging" && relatorio.data && !Array.isArray(relatorio.data) && (
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500">A Vencer</p>
                <p className="text-lg font-bold text-green-600">R$ {Number(relatorio.data.aVencer || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500">1-15 dias</p>
                <p className="text-lg font-bold text-yellow-600">R$ {Number(relatorio.data.de1a15 || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500">16-30 dias</p>
                <p className="text-lg font-bold text-orange-600">R$ {Number(relatorio.data.de16a30 || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500">+30 dias</p>
                <p className="text-lg font-bold text-red-600">R$ {Number(relatorio.data.acima30 || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          )}

          {tipo !== "aging" && Array.isArray(relatorio.data) && relatorio.data.length > 0 && (
            <div className="space-y-4">
              {/* Gráfico simples com barras CSS */}
              {tipoGrafico === "bar" && (
                <div className="space-y-2">
                  {relatorio.data.map((item: any, idx: number) => {
                    const label = item.periodo || item.fornecedor || item.categoria || `Item ${idx + 1}`;
                    const valor = Number(item.total || 0);
                    const max = Math.max(...relatorio.data.map((d: any) => Number(d.total || 0)));
                    const pct = max > 0 ? (valor / max) * 100 : 0;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="w-32 truncate text-right text-xs">{label}</span>
                        <div className="flex-1 bg-gray-100 rounded h-6 relative">
                          <div className="bg-blue-500 h-6 rounded" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-28 text-right text-xs font-mono">R$ {valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {tipoGrafico === "line" && (
                <div className="h-48 flex items-end gap-1 border-b border-l p-2">
                  {relatorio.data.map((item: any, idx: number) => {
                    const valor = Number(item.total || 0);
                    const max = Math.max(...relatorio.data.map((d: any) => Number(d.total || 0)));
                    const pct = max > 0 ? (valor / max) * 100 : 0;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full">
                        <div className="w-2 bg-blue-500 rounded-t" style={{ height: `${pct}%` }} />
                        <span className="text-[9px] mt-1 truncate w-full text-center">{(item.periodo || item.fornecedor || item.categoria || "").substring(0, 7)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {tipoGrafico === "pie" && (
                <div className="flex flex-wrap gap-2">
                  {relatorio.data.map((item: any, idx: number) => {
                    const label = item.periodo || item.fornecedor || item.categoria || `Item ${idx + 1}`;
                    const valor = Number(item.total || 0);
                    const totalGeral = relatorio.data.reduce((acc: number, d: any) => acc + Number(d.total || 0), 0);
                    const pct = totalGeral > 0 ? ((valor / totalGeral) * 100).toFixed(1) : "0";
                    const colors = ["bg-blue-500", "bg-green-500", "bg-yellow-500", "bg-red-500", "bg-purple-500", "bg-pink-500", "bg-indigo-500", "bg-orange-500"];
                    return (
                      <div key={idx} className="flex items-center gap-2 text-xs border rounded px-2 py-1">
                        <div className={`w-3 h-3 rounded ${colors[idx % colors.length]}`} />
                        <span className="truncate max-w-[120px]">{label}</span>
                        <span className="font-mono">{pct}%</span>
                        <span className="text-gray-400">R$ {valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tabela de dados */}
              <div className="border rounded overflow-x-auto mt-4">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">{tipo === "fluxo" ? "Período" : tipo === "fornecedor" ? "Fornecedor" : "Categoria"}</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2 text-right">Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.data.map((item: any, idx: number) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{item.periodo || item.fornecedor || item.categoria}</td>
                        <td className="p-2 text-right font-mono">R$ {Number(item.total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                        <td className="p-2 text-right">{item.quantidade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tipo !== "aging" && Array.isArray(relatorio.data) && relatorio.data.length === 0 && (
            <p className="text-gray-400 text-sm text-center">Nenhum dado para o período selecionado</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ============================================================
// ABA FORNECEDORES
// ============================================================
function AbaFornecedores() {
  const fornecedores = trpc.contasPagar.fornecedores.list.useQuery();
  const utils = trpc.useUtils();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<any>({});

  const createMut = trpc.contasPagar.fornecedores.create.useMutation({
    onSuccess: () => { toast.success("Fornecedor criado"); setShowForm(false); setForm({}); utils.contasPagar.fornecedores.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.contasPagar.fornecedores.update.useMutation({
    onSuccess: () => { toast.success("Fornecedor atualizado"); setEditId(null); setForm({}); utils.contasPagar.fornecedores.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMut = trpc.contasPagar.fornecedores.delete.useMutation({
    onSuccess: () => { toast.success("Fornecedor removido"); utils.contasPagar.fornecedores.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = () => {
    if (!form.razaoSocial?.trim()) { toast.error("Razão Social obrigatória"); return; }
    if (editId) {
      updateMut.mutate({ id: editId, ...form });
    } else {
      createMut.mutate(form);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Fornecedores</h2>
        <Button size="sm" onClick={() => { setShowForm(true); setEditId(null); setForm({}); }}>
          <Plus className="h-4 w-4 mr-1" />Novo Fornecedor
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Razão Social *</Label>
                <Input value={form.razaoSocial || ""} onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })} />
              </div>
              <div>
                <Label>Nome Fantasia</Label>
                <Input value={form.nomeFantasia || ""} onChange={(e) => setForm({ ...form, nomeFantasia: e.target.value })} />
              </div>
              <div>
                <Label>CNPJ</Label>
                <Input value={form.cnpj || ""} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} />
              </div>
              <div>
                <Label>Contato</Label>
                <Input value={form.contato || ""} onChange={(e) => setForm({ ...form, contato: e.target.value })} />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Observações</Label>
                <Input value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending}>
                {editId ? "Salvar" : "Criar"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); setForm({}); }}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left">Razão Social</th>
              <th className="p-2 text-left">Nome Fantasia</th>
              <th className="p-2 text-left">CNPJ</th>
              <th className="p-2 text-left">Contato</th>
              <th className="p-2 text-left">Telefone</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-center w-32">Ações</th>
            </tr>
          </thead>
          <tbody>
            {fornecedores.data?.map?.((f: any) => (
              <tr key={f.id} className="border-t">
                <td className="p-2">{f.razaoSocial}</td>
                <td className="p-2">{f.nomeFantasia || "-"}</td>
                <td className="p-2">{f.cnpj || "-"}</td>
                <td className="p-2">{f.contato || "-"}</td>
                <td className="p-2">{f.telefone || "-"}</td>
                <td className="p-2">{f.email || "-"}</td>
                <td className="p-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <button className="text-blue-600 text-xs hover:underline" onClick={() => { setEditId(f.id); setForm(f); setShowForm(true); }}>Editar</button>
                    <button className="text-red-600 text-xs hover:underline" onClick={() => { if (confirm("Remover fornecedor?")) deleteMut.mutate({ id: f.id }); }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
            {(!fornecedores.data?.length) && (
              <tr><td colSpan={7} className="p-4 text-center text-gray-400">Nenhum fornecedor cadastrado</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// ABA CENTROS DE CUSTO
// ============================================================
function AbaCentrosCusto() {
  const centrosCusto = trpc.contasPagar.centrosCusto.list.useQuery();
  const utils = trpc.useUtils();
  const [novoNome, setNovoNome] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editNome, setEditNome] = useState("");

  const createMut = trpc.contasPagar.centrosCusto.create.useMutation({
    onSuccess: () => { toast.success("Centro de custo criado"); setNovoNome(""); utils.contasPagar.centrosCusto.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.contasPagar.centrosCusto.update.useMutation({
    onSuccess: () => { toast.success("Centro de custo atualizado"); setEditId(null); utils.contasPagar.centrosCusto.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMut = trpc.contasPagar.centrosCusto.delete.useMutation({
    onSuccess: () => { toast.success("Centro de custo removido"); utils.contasPagar.centrosCusto.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4 max-w-lg">
      <div className="flex gap-2">
        <Input placeholder="Novo centro de custo..." value={novoNome} onChange={(e) => setNovoNome(e.target.value)} />
        <Button size="sm" onClick={() => { if (novoNome.trim()) createMut.mutate({ nome: novoNome.trim() }); }} disabled={createMut.isPending}>
          <Plus className="h-4 w-4 mr-1" />Criar
        </Button>
      </div>
      <div className="border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left">Nome</th>
              <th className="p-2 text-center w-32">Ações</th>
            </tr>
          </thead>
          <tbody>
            {centrosCusto.data?.map?.((c: any) => (
              <tr key={c.id} className="border-t">
                <td className="p-2">
                  {editId === c.id ? (
                    <Input value={editNome} onChange={(e) => setEditNome(e.target.value)} className="h-7" />
                  ) : (
                    c.nome
                  )}
                </td>
                <td className="p-2 text-center">
                  {editId === c.id ? (
                    <div className="flex gap-1 justify-center">
                      <button className="text-green-600 text-xs hover:underline" onClick={() => updateMut.mutate({ id: c.id, nome: editNome })}>Salvar</button>
                      <button className="text-gray-600 text-xs hover:underline" onClick={() => setEditId(null)}>Cancelar</button>
                    </div>
                  ) : (
                    <div className="flex gap-1 justify-center">
                      <button className="text-blue-600 text-xs hover:underline" onClick={() => { setEditId(c.id); setEditNome(c.nome); }}>Editar</button>
                      <button className="text-red-600 text-xs hover:underline" onClick={() => { if (confirm("Remover centro de custo?")) deleteMut.mutate({ id: c.id }); }}>Excluir</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {(!centrosCusto.data?.length) && (
              <tr><td colSpan={2} className="p-4 text-center text-gray-400">Nenhum centro de custo cadastrado</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// ABA IMPORTAR EM MASSA
// ============================================================
function AbaImportar() {
  const utils = trpc.useUtils();
  const fornecedores = trpc.contasPagar.fornecedores.list.useQuery();
  const categorias = trpc.contasPagar.categorias.list.useQuery();
  const centrosCusto = trpc.contasPagar.centrosCusto.list.useQuery();

  const [etapa, setEtapa] = useState<"upload" | "validacao" | "resultado">("upload");
  const [linhasValidadas, setLinhasValidadas] = useState<any[]>([]);
  const [resultado, setResultado] = useState<{ importados: number; erros: string[] } | null>(null);
  const fileInputRef = useState<HTMLInputElement | null>(null);

  const importarMut = trpc.contasPagar.importarEmMassa.useMutation({
    onSuccess: (data) => {
      setResultado(data);
      setEtapa("resultado");
      utils.contasPagar.invalidate();
      toast.success(`${data.importados} contas importadas`);
    },
    onError: (e) => toast.error(e.message),
  });

  const downloadModelo = () => {
    const modelo = [
      {
        descricao: "Exemplo - Aluguel",
        valor: 2500.00,
        dataVencimento: "2026-07-15",
        dataEmissao: "2026-07-01",
        mesCompetencia: "2026-07",
        fornecedor_cnpj: "12.345.678/0001-90",
        categoria: "Administrativo",
        centro_custo: "Sede",
        tipoPagamento: "boleto",
        dadoPagamento: "23793.38128 60000.000003 00000.000400 1 90000000250000",
        chaveAcesso: "",
        observacoes: "Parcela 1/12",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(modelo);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contas a Pagar");
    XLSX.writeFile(wb, "modelo_contas_pagar.xlsx");
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const data = new Uint8Array(ev.target?.result as ArrayBuffer);
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws);

      // Converter serial numérico do Excel para data YYYY-MM-DD
      const excelSerialToDate = (v: any): string | undefined => {
        if (!v) return undefined;
        if (typeof v === "number") {
          const d = new Date(Math.round((v - 25569) * 86400 * 1000));
          const yyyy = d.getUTCFullYear();
          const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        return String(v);
      };

      // Validar cada linha
      const validadas = rows.map((row, idx) => {
        row.dataVencimento = excelSerialToDate(row.dataVencimento);
        if (row.dataEmissao) row.dataEmissao = excelSerialToDate(row.dataEmissao);
        const erros: string[] = [];
        const alertas: string[] = [];

        if (!row.descricao) erros.push("Descrição obrigatória");
        if (!row.valor || isNaN(Number(row.valor))) erros.push("Valor inválido");
        if (!row.dataVencimento) erros.push("Data vencimento obrigatória");

        // Resolver fornecedor por CNPJ
        let fornecedorId: number | null = null;
        if (row.fornecedor_cnpj) {
          const cnpjLimpo = String(row.fornecedor_cnpj).replace(/\D/g, "");
          const forn = (fornecedores.data as any)?.find?.((f: any) => f.cnpj?.replace(/\D/g, "") === cnpjLimpo);
          if (forn) {
            fornecedorId = forn.id;
          } else {
            alertas.push("Fornecedor não encontrado pelo CNPJ");
          }
        }

        // Resolver categoria por nome
        let categoriaId: number | null = null;
        if (row.categoria) {
          const cat = categorias.data?.find?.((c: any) => c.nome.toLowerCase() === String(row.categoria).toLowerCase());
          if (cat) {
            categoriaId = cat.id;
          } else {
            alertas.push("Categoria não encontrada");
          }
        }

        // Resolver centro de custo por nome
        let centroCustoId: number | null = null;
        if (row.centro_custo) {
          const cc = centrosCusto.data?.find?.((c: any) => c.nome.toLowerCase() === String(row.centro_custo).toLowerCase());
          if (cc) {
            centroCustoId = cc.id;
          } else {
            alertas.push("Centro de custo não encontrado");
          }
        }

        return {
          linha: idx + 2,
          ...row,
          fornecedorId,
          categoriaId,
          centroCustoId,
          valor: Number(row.valor) || 0,
          erros,
          alertas,
        };
      });

      setLinhasValidadas(validadas);
      setEtapa("validacao");
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const temErros = linhasValidadas.some((l) => l.erros.length > 0);

  const handleConfirmar = () => {
    const contas = linhasValidadas.map((l) => ({
      descricao: l.descricao || "",
      valor: l.valor,
      dataVencimento: l.dataVencimento || "",
      dataEmissao: l.dataEmissao || null,
      mesCompetencia: l.mesCompetencia || null,
      fornecedorId: l.fornecedorId,
      categoriaId: l.categoriaId,
      centroCustoId: l.centroCustoId,
      tipoPagamento: l.tipoPagamento || null,
      dadoPagamento: l.dadoPagamento || null,
      chaveAcesso: l.chaveAcesso || null,
      observacoes: l.observacoes || null,
    }));
    importarMut.mutate({ contas });
  };

  if (etapa === "resultado" && resultado) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <p className="text-lg font-semibold">{resultado.importados} contas importadas com sucesso</p>
            {resultado.erros.length > 0 && (
              <div className="text-left bg-red-50 border border-red-200 rounded p-3 mt-3">
                <p className="text-sm font-medium text-red-700 mb-1">Erros ({resultado.erros.length}):</p>
                {resultado.erros.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
              </div>
            )}
            <Button onClick={() => { setEtapa("upload"); setLinhasValidadas([]); setResultado(null); }}>Nova Importação</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (etapa === "validacao") {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Validação — {linhasValidadas.length} linhas</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setEtapa("upload"); setLinhasValidadas([]); }}>Voltar</Button>
            <Button size="sm" onClick={handleConfirmar} disabled={temErros || importarMut.isPending}>
              {importarMut.isPending ? "Importando..." : "Confirmar Importação"}
            </Button>
          </div>
        </div>
        {temErros && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-sm text-red-700">Existem erros que impedem a importação. Corrija o Excel e tente novamente.</p>
          </div>
        )}
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Linha</th>
                <th className="p-2 text-left">Descrição</th>
                <th className="p-2 text-right">Valor</th>
                <th className="p-2 text-left">Vencimento</th>
                <th className="p-2 text-left">Fornecedor</th>
                <th className="p-2 text-left">Categoria</th>
                <th className="p-2 text-left">Centro Custo</th>
                <th className="p-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {linhasValidadas.map((l, i) => (
                <tr key={i} className={`border-t ${l.erros.length > 0 ? "bg-red-50" : l.alertas.length > 0 ? "bg-yellow-50" : ""}`}>
                  <td className="p-2">{l.linha}</td>
                  <td className="p-2">{l.descricao || "-"}</td>
                  <td className="p-2 text-right">R$ {Number(l.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  <td className="p-2">{l.dataVencimento || "-"}</td>
                  <td className="p-2">{l.fornecedor_cnpj || "-"}</td>
                  <td className="p-2">{l.categoria || "-"}</td>
                  <td className="p-2">{l.centro_custo || "-"}</td>
                  <td className="p-2">
                    {l.erros.length > 0 && <span className="text-red-600">{l.erros.join("; ")}</span>}
                    {l.alertas.length > 0 && <span className="text-yellow-600">{l.alertas.join("; ")}</span>}
                    {l.erros.length === 0 && l.alertas.length === 0 && <span className="text-green-600">OK</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Etapa upload
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-semibold">Importar Contas a Pagar em Massa</h2>
          <p className="text-sm text-gray-600">Faça o download do modelo, preencha com os dados e faça o upload para importar.</p>
          <div className="flex gap-3">
            <Button size="sm" variant="outline" onClick={downloadModelo}>
              <Download className="h-4 w-4 mr-1" />Baixar Modelo
            </Button>
            <label className="cursor-pointer">
              <Button size="sm" asChild>
                <span><Upload className="h-4 w-4 mr-1" />Upload Excel</span>
              </Button>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} />
            </label>
          </div>
          <div className="text-xs text-gray-500 space-y-1">
            <p><strong>Colunas obrigatórias:</strong> descricao, valor, dataVencimento (YYYY-MM-DD)</p>
            <p><strong>Colunas opcionais:</strong> dataEmissao, mesCompetencia, fornecedor_cnpj, categoria, centro_custo, tipoPagamento, dadoPagamento, chaveAcesso, observacoes</p>
            <p><strong>Fornecedor:</strong> resolvido pelo CNPJ (deve estar cadastrado na aba Fornecedores)</p>
            <p><strong>Categoria/Centro de Custo:</strong> resolvidos pelo nome exato (devem estar cadastrados)</p>
          </div>
        </CardContent>
      </Card>

      <ImportarOutros fornecedores={fornecedores.data || []} categorias={categorias.data || []} centrosCusto={centrosCusto.data || []} />
    </div>
  );
}

// ============================================================
// IMPORTAR OUTROS — Upload XML/PDF com parse local + Gemini IA
// ============================================================

interface ArquivoImportado {
  id: string;
  file: File;
  nomeArquivo: string;
  status: "pendente" | "analisando" | "sucesso" | "erro" | "analisando_ia";
  erro?: string;
  dados?: {
    fornecedorCnpj?: string;
    fornecedorNome?: string;
    chaveAcesso?: string;
    valorTotal?: number;
    dataEmissao?: string;
    dataVencimento?: string;
    tipoPagamento?: string;
    dadoPagamento?: string;
    descricao?: string;
    parcelas?: Array<{ numero: number; valor: number; vencimento: string }>;
  };
  aceito?: boolean;
}

function ImportarOutros({ fornecedores, categorias, centrosCusto }: { fornecedores: any[]; categorias: any[]; centrosCusto: any[] }) {
  const utils = trpc.useUtils();
  const [arquivos, setArquivos] = useState<ArquivoImportado[]>([]);
  const [criando, setCriando] = useState(false);

  const analisarMut = trpc.contasPagar.analisarArquivo.useMutation();
  const analisarIAMut = trpc.contasPagar.analisarArquivoIA.useMutation();
  const criarMut = trpc.contasPagar.create.useMutation({
    onError: (e) => toast.error(e.message),
  });

  const handleFilesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const novosArquivos: ArquivoImportado[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      novosArquivos.push({
        id: crypto.randomUUID(),
        file,
        nomeArquivo: file.name,
        status: "pendente",
      });
    }

    setArquivos((prev) => [...prev, ...novosArquivos]);

    // Analisar cada arquivo automaticamente (parse local)
    for (const arq of novosArquivos) {
      await analisarArquivoLocal(arq);
    }

    e.target.value = "";
  };

  const analisarArquivoLocal = async (arq: ArquivoImportado) => {
    setArquivos((prev) => prev.map((a) => a.id === arq.id ? { ...a, status: "analisando" } : a));
    try {
      const base64 = await fileToBase64(arq.file);
      const result = await analisarMut.mutateAsync({
        nomeArquivo: arq.nomeArquivo,
        conteudo: base64,
        mimeType: arq.file.type || (arq.nomeArquivo.endsWith(".xml") ? "text/xml" : "application/pdf"),
      });
      if (result.sucesso && result.dados) {
        setArquivos((prev) => prev.map((a) => a.id === arq.id ? { ...a, status: "sucesso", dados: result.dados } : a));
      } else {
        setArquivos((prev) => prev.map((a) => a.id === arq.id ? { ...a, status: "erro", erro: result.erro || "Não foi possível extrair dados" } : a));
      }
    } catch (err: any) {
      setArquivos((prev) => prev.map((a) => a.id === arq.id ? { ...a, status: "erro", erro: err.message || "Erro ao analisar" } : a));
    }
  };

  const analisarComIA = async (arqId: string) => {
    const arq = arquivos.find((a) => a.id === arqId);
    if (!arq) return;
    setArquivos((prev) => prev.map((a) => a.id === arqId ? { ...a, status: "analisando_ia" } : a));
    try {
      const base64 = await fileToBase64(arq.file);
      const result = await analisarIAMut.mutateAsync({
        nomeArquivo: arq.nomeArquivo,
        conteudo: base64,
        mimeType: arq.file.type || (arq.nomeArquivo.endsWith(".xml") ? "text/xml" : "application/pdf"),
      });
      if (result.sucesso && result.dados) {
        setArquivos((prev) => prev.map((a) => a.id === arqId ? { ...a, status: "sucesso", dados: result.dados, erro: undefined } : a));
        toast.success(`IA analisou ${arq.nomeArquivo} com sucesso`);
      } else {
        setArquivos((prev) => prev.map((a) => a.id === arqId ? { ...a, status: "erro", erro: result.erro || "IA não conseguiu extrair dados" } : a));
      }
    } catch (err: any) {
      setArquivos((prev) => prev.map((a) => a.id === arqId ? { ...a, status: "erro", erro: err.message || "Erro na análise com IA" } : a));
    }
  };

  const removerArquivo = (arqId: string) => {
    setArquivos((prev) => prev.filter((a) => a.id !== arqId));
  };

  const aceitarDados = (arqId: string) => {
    setArquivos((prev) => prev.map((a) => a.id === arqId ? { ...a, aceito: true } : a));
  };

  const confirmarImportacao = async () => {
    const aceitos = arquivos.filter((a) => a.aceito && a.dados);
    if (aceitos.length === 0) {
      toast.error("Nenhum arquivo aceito para importar");
      return;
    }
    setCriando(true);
    let criados = 0;
    let erros = 0;
    for (const arq of aceitos) {
      const d = arq.dados!;
      // Se tem parcelas, criar uma conta por parcela
      const parcelas = d.parcelas && d.parcelas.length > 0 ? d.parcelas : [{ numero: 1, valor: d.valorTotal || 0, vencimento: d.dataVencimento || "" }];
      const agrupadorId = parcelas.length > 1 ? crypto.randomUUID() : undefined;
      for (const parcela of parcelas) {
        try {
          await criarMut.mutateAsync({
            descricao: d.descricao || arq.nomeArquivo,
            valor: parcela.valor,
            dataVencimento: parcela.vencimento || d.dataVencimento || "",
            dataEmissao: d.dataEmissao || null,
            fornecedorId: resolverFornecedor(d.fornecedorCnpj),
            tipoPagamento: d.tipoPagamento || null,
            dadoPagamento: d.dadoPagamento || null,
            chaveAcesso: d.chaveAcesso || null,
            parcelaAtual: parcelas.length > 1 ? parcela.numero : null,
            totalParcelas: parcelas.length > 1 ? parcelas.length : null,
            agrupadorId: agrupadorId || null,
            observacoes: `Importado de: ${arq.nomeArquivo}`,
          });
          criados++;
        } catch {
          erros++;
        }
      }
    }
    setCriando(false);
    utils.contasPagar.invalidate();
    toast.success(`${criados} lançamento(s) criado(s)${erros > 0 ? `, ${erros} erro(s)` : ""}`);
    // Remover os aceitos da lista
    setArquivos((prev) => prev.filter((a) => !a.aceito));
  };

  const resolverFornecedor = (cnpj?: string): number | null => {
    if (!cnpj) return null;
    const cnpjLimpo = cnpj.replace(/\D/g, "");
    const forn = fornecedores.find((f: any) => f.cnpj?.replace(/\D/g, "") === cnpjLimpo);
    return forn ? forn.id : null;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove o prefixo data:...;base64,
        const base64 = result.split(",")[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const arquivosAceitos = arquivos.filter((a) => a.aceito);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Importar Outros (XML NFe / PDF)</h2>
        <p className="text-sm text-gray-600">Faça upload de arquivos XML de NFe ou PDF de boletos/DANFE. O sistema tentará extrair os dados automaticamente.</p>

        <label className="cursor-pointer">
          <Button size="sm" asChild>
            <span><Upload className="h-4 w-4 mr-1" />Upload Arquivos (XML/PDF)</span>
          </Button>
          <input type="file" accept=".xml,.pdf" multiple className="hidden" onChange={handleFilesUpload} />
        </label>

        {arquivos.length > 0 && (
          <div className="space-y-3 mt-4">
            {arquivos.map((arq) => (
              <div key={arq.id} className={`border rounded p-3 space-y-2 ${arq.aceito ? "bg-green-50 border-green-200" : arq.status === "erro" ? "bg-red-50 border-red-200" : "bg-white"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">{arq.nomeArquivo}</span>
                    {arq.status === "analisando" && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                    {arq.status === "analisando_ia" && <Loader2 className="h-4 w-4 animate-spin text-purple-500" />}
                    {arq.status === "sucesso" && <CheckCircle className="h-4 w-4 text-green-500" />}
                    {arq.status === "erro" && <AlertTriangle className="h-4 w-4 text-red-500" />}
                    {arq.aceito && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Aceito</span>}
                  </div>
                  <button onClick={() => removerArquivo(arq.id)} className="text-gray-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {arq.status === "erro" && arq.erro && (
                  <p className="text-xs text-red-600">{arq.erro}</p>
                )}

                {arq.status === "sucesso" && arq.dados && !arq.aceito && (
                  <div className="text-xs space-y-1 bg-gray-50 rounded p-2">
                    {arq.dados.fornecedorNome && <p><strong>Fornecedor:</strong> {arq.dados.fornecedorNome} {arq.dados.fornecedorCnpj ? `(${arq.dados.fornecedorCnpj})` : ""}</p>}
                    {arq.dados.valorTotal != null && <p><strong>Valor:</strong> R$ {Number(arq.dados.valorTotal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>}
                    {arq.dados.dataEmissao && <p><strong>Emissão:</strong> {arq.dados.dataEmissao}</p>}
                    {arq.dados.dataVencimento && <p><strong>Vencimento:</strong> {arq.dados.dataVencimento}</p>}
                    {arq.dados.chaveAcesso && <p><strong>Chave:</strong> {arq.dados.chaveAcesso}</p>}
                    {arq.dados.tipoPagamento && <p><strong>Pagamento:</strong> {arq.dados.tipoPagamento} {arq.dados.dadoPagamento ? `— ${arq.dados.dadoPagamento}` : ""}</p>}
                    {arq.dados.descricao && <p><strong>Descrição:</strong> {arq.dados.descricao}</p>}
                    {arq.dados.parcelas && arq.dados.parcelas.length > 1 && (
                      <div>
                        <strong>Parcelas ({arq.dados.parcelas.length}):</strong>
                        <ul className="ml-3">
                          {arq.dados.parcelas.map((p, i) => (
                            <li key={i}>{p.numero}x — R$ {p.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} — Venc: {p.vencimento}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => aceitarDados(arq.id)}>
                        <CheckCircle className="h-3 w-3 mr-1" />Aceitar
                      </Button>
                    </div>
                  </div>
                )}

                {(arq.status === "erro" || (arq.status === "sucesso" && !arq.aceito)) && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => analisarComIA(arq.id)} disabled={arq.status === "analisando_ia"}>
                      <Sparkles className="h-3 w-3 mr-1" />Analisar com IA
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {arquivosAceitos.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <Button onClick={confirmarImportacao} disabled={criando}>
                  {criando ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Criando...</> : <><CheckCircle className="h-4 w-4 mr-1" />Confirmar Importação ({arquivosAceitos.length} arquivo(s))</>}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componente de código de barras para boleto
function BoletoBarcode({ linhaDigitavel }: { linhaDigitavel: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !linhaDigitavel) return;
    // Remover pontos e espaços da linha digitável para gerar o código de barras
    const codigoLimpo = linhaDigitavel.replace(/[.\s]/g, "");
    try {
      JsBarcode(canvasRef.current, codigoLimpo, {
        format: "ITF",
        width: 1,
        height: 50,
        displayValue: false,
        margin: 4,
      });
    } catch {
      // Se ITF falhar (ex: número ímpar de dígitos), tentar CODE128
      try {
        JsBarcode(canvasRef.current, codigoLimpo, {
          format: "CODE128",
          width: 1,
          height: 50,
          displayValue: false,
          margin: 4,
        });
      } catch {
        // silenciar erro
      }
    }
  }, [linhaDigitavel]);

  return (
    <div className="border rounded p-3">
      <p className="text-sm font-medium mb-2">Código de Barras</p>
      <div className="flex justify-center overflow-x-auto">
        <canvas ref={canvasRef} />
      </div>
      <p className="text-xs text-center text-gray-500 mt-1 break-all">{linhaDigitavel}</p>
    </div>
  );
}
